import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BOOK_DISPLAY_CURRENCY_STORAGE_KEY,
  sortCurrencySelectorOptions,
} from "@/utils/book-display-currency";

function norm(code: string): string {
  return code.trim().toUpperCase();
}

export interface UseBookDisplayCurrencyParams {
  /**
   * Currencies that appear on priced availability / product payloads — used only when
   * `configuration/get` omits `CurrencyCodes` (fallback picker list).
   */
  payloadCurrencyKeys: string[];
  /**
   * From `configuration/get` `CurrencyCodes` — when non-empty, the **full** picker uses this list
   * (enterprise order), without requiring each code to appear on the current price payload.
   */
  enterpriseCurrencyCodes?: readonly string[] | null;
  /**
   * From `hotels/get` — applied only in the **fallback** path when configuration has no `CurrencyCodes`.
   */
  acceptedCurrencyCodes?: string[];
  /** From `hotels/get` — preferred default when that code appears in options. */
  defaultCurrencyCode?: string;
}

export interface UseBookDisplayCurrencyResult {
  /** For pricing UI: CHF when fewer than two selectable currencies; otherwise the picked code. */
  displayCurrency: string;
  setDisplayCurrency: (code: string) => void;
  /** Full `CurrencyCodes` when configured; else payload-derived list. */
  selectorOptions: string[];
  /** True when the guest can choose among multiple currencies. */
  showCurrencyPicker: boolean;
}

/**
 * Book-page display currency: localStorage persistence and sync when options change.
 *
 * When `configuration/get` returns **`CurrencyCodes`**, those codes are **all** selectable (in API order),
 * even if a given code has no amount on the current browse payload — `pickDisplayAmount` / formatters
 * still resolve amounts from Mews maps with CHF (etc.) fallback.
 *
 * When there is no multi-currency list (`selectorOptions.length < 2`), **display is locked to CHF**
 * with no picker.
 */
export function useBookDisplayCurrency(
  params: UseBookDisplayCurrencyParams
): UseBookDisplayCurrencyResult {
  const {
    payloadCurrencyKeys,
    enterpriseCurrencyCodes,
    acceptedCurrencyCodes,
    defaultCurrencyCode,
  } = params;

  const selectorOptions = useMemo(() => {
    const hasEnterprise =
      enterpriseCurrencyCodes != null && enterpriseCurrencyCodes.length > 0;

    if (hasEnterprise) {
      const unique = [
        ...new Set(enterpriseCurrencyCodes.map(norm)),
      ].filter(Boolean);
      return unique.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    }

    const payload = [...new Set(payloadCurrencyKeys.map(norm))].filter(Boolean);
    const hasAccepted =
      acceptedCurrencyCodes != null && acceptedCurrencyCodes.length > 0;
    const acceptedSet = hasAccepted
      ? new Set(acceptedCurrencyCodes.map(norm))
      : null;

    let options = payload;
    if (acceptedSet) {
      options = options.filter((c) => acceptedSet.has(c));
    }

    if (options.length === 0 && acceptedSet) {
      const relaxed = payload.filter((c) => acceptedSet.has(c));
      if (relaxed.length > 0) {
        return sortCurrencySelectorOptions(relaxed, null);
      }
    }

    if (options.length === 0) {
      return sortCurrencySelectorOptions(payload, null);
    }

    return sortCurrencySelectorOptions(options, null);
  }, [payloadCurrencyKeys, enterpriseCurrencyCodes, acceptedCurrencyCodes]);

  const showCurrencyPicker = selectorOptions.length > 1;

  const effectiveDefault = useMemo(() => {
    if (selectorOptions.length === 0) return "CHF";
    const hotelDefault = norm(defaultCurrencyCode ?? "");
    if (hotelDefault && selectorOptions.includes(hotelDefault)) {
      return hotelDefault;
    }
    if (selectorOptions.includes("CHF")) return "CHF";
    return selectorOptions[0];
  }, [selectorOptions, defaultCurrencyCode]);

  const [displayCurrency, setDisplayCurrencyState] = useState(effectiveDefault);

  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!showCurrencyPicker) {
      hydratedRef.current = false;
      setDisplayCurrencyState("CHF");
      try {
        localStorage.setItem(BOOK_DISPLAY_CURRENCY_STORAGE_KEY, "CHF");
      } catch {
        /* ignore */
      }
      return;
    }

    if (!hydratedRef.current) {
      hydratedRef.current = true;
      let stored: string | null = null;
      try {
        stored = localStorage.getItem(BOOK_DISPLAY_CURRENCY_STORAGE_KEY);
      } catch {
        /* private mode / quota */
      }
      const fromStorage = stored ? norm(stored) : "";
      if (fromStorage && selectorOptions.includes(fromStorage)) {
        setDisplayCurrencyState(fromStorage);
        return;
      }
      setDisplayCurrencyState(effectiveDefault);
      return;
    }

    if (!selectorOptions.includes(norm(displayCurrency))) {
      const next = effectiveDefault;
      setDisplayCurrencyState(next);
      try {
        localStorage.setItem(BOOK_DISPLAY_CURRENCY_STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
    }
  }, [selectorOptions, effectiveDefault, displayCurrency, showCurrencyPicker]);

  const setDisplayCurrency = useCallback(
    (code: string) => {
      if (!showCurrencyPicker) return;
      const n = norm(code);
      if (!selectorOptions.includes(n)) return;
      setDisplayCurrencyState(n);
      try {
        localStorage.setItem(BOOK_DISPLAY_CURRENCY_STORAGE_KEY, n);
      } catch {
        /* ignore */
      }
    },
    [selectorOptions, showCurrencyPicker]
  );

  const resolvedDisplayCurrency = showCurrencyPicker ? norm(displayCurrency) : "CHF";

  return {
    displayCurrency: resolvedDisplayCurrency,
    setDisplayCurrency,
    selectorOptions,
    showCurrencyPicker,
  };
}
